<template>
  <div id="app">
    <UrlParams v-model="pageParams" />

    <div
      class="container m-block__code f-rte f-rte--block"
      v-if="practices && !practice"
    >
      <ControlGroup>
        <DataGetter
          :url="`${apiRoute}/list/Country`"
          v-slot="{ data }"
          :child-element-type="null"
        >
          <Multiselect
            v-if="data"
            key="countriesdd"
            label="Countries"
            v-model="countries"
            :options="[...data].sort() || []"
            :defaultOption="formatMultiselectLabel(countries)"
          />
        </DataGetter>
        <DataGetter
          :url="`${apiRoute}/list/Sector`"
          v-slot="{ data }"
          :child-element-type="null"
        >
          <Multiselect
            v-if="data"
            key="sectorsdd"
            label="Sectors"
            v-model="sectors"
            :options="data || []"
            :defaultOption="formatMultiselectLabel(sectors)"
          />
        </DataGetter>
      </ControlGroup>

      
      <ControlGroup>
        <DataGetter
          :url="`${apiRoute}/list/Methodology`"
          v-slot="{ data }"
          :child-element-type="null"
        >
          <Multiselect
            v-if="data"
            key="methodologiesdd"
            label="Methodologies"
            v-model="methodologies"
            :options="data || []"
            :defaultOption="formatMultiselectLabel(methodologies)"
          />
        </DataGetter>

        <DataGetter
          :url="`${apiRoute}/list/Documentation_type`"
          v-slot="{ data }"
          :child-element-type="null"
        > 
          <Multiselect
            v-if="data"
            key="documentationtypesdd"
            label="Documentation types"
            v-model="documentationTypes"
            :options="data || []"
            :defaultOption="formatMultiselectLabel(documentationTypes)"
          />
        </DataGetter>
      </ControlGroup>

      <ControlGroup>
        <StyledInput key="searchinput" label="Search" v-model="search" />
      </ControlGroup>

      <div class="filter-actions">
        <StyledButton
          :onClick="resetFilters"
          :disabled="!countries.length && !sectors.length && !methodologies.length && !documentationTypes.length && !search"
        >
          Reset
        </StyledButton>
        <span class="f-ui-3">
          {{ practices.length }} practice{{practices.length > 1 ? 's' : ''}} found
        </span>
      </div>


      <SortableTable
        :headers="headers"
        :items="practices || []"
        animated
        selectable
        @rowSelected="openPracticePage"
      />
    </div>

    <div class="container m-block__code f-rte f-rte--block" v-if="practice">
      <div class="container-header" style="display: flex; justify-content: space-between; align-items: center">
        <h4 class="m-block__title f-title-7">{{ practice.Title }} <br /> {{ practice.Methodology }} for {{ practice.Sector }}</h4>
        <a @click="closePracticePage" class="m-nav-page__infos-close-btn" title="close">
          <Icon icon="close-btn" />
        </a>
      </div>

      <CustomBox subtle title="Background" :indicators="practiceBackgroundInfo"/>
      <CustomBox subtle title="Data collection" :indicators="practiceDataCollectionInfo"/>
      <CustomBox subtle title="Comments" :indicators="practiceCommentsInfo" />
    </div>
  </div>
</template>

<script>
import {
  DataGetter,
  ControlGroup,
  Multiselect,
  StyledButton,
  Icon,
  UrlParams,
  StyledInput,
  SortableTable
} from "@international-energy-agency/vue-ui";
import axios from "axios";
import CustomBox from "./CustomBox";

export default {
  name: "App",
  components: {
    DataGetter,
    ControlGroup,
    Multiselect,
    StyledInput,
    SortableTable,
    UrlParams,
    StyledButton,
    Icon,
    CustomBox,
  },
  props: {
    test: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      apiRoute: this.test ? "https://api.test.iea.org/country-practices" : "https://api.iea.org/country-practices",
      // apiRoute: "http://localhost:3000/country-practices",
      pageParams: {},
      countries: [],
      sectors: [],
      methodologies: [],
      documentationTypes: [],
      search: "",
      allPractices: [],
      practices: [],
      practice: null,
      headers: [
        { key: "Title", label: "Practice", sortable: true },
        { key: "Sector", label: "Sector", sortable: true },
        { key: "Methodology", label: "Methodology", sortable: true },
        { key: "Documentation_type", label: "Available content", sortable: true },
      ],
      searchTimeout: null,
    };
  },
  watch: {
    pageParams() {
      if (this.pageParams.viewPractice && !this.practice) {
        this.fetchPractice(this.pageParams.viewPractice);
      } else if (!this.pageParams.viewPractice && !this.practices.length) {
        this.practice = null;
        this.fetchPractices();
      } else if (!this.pageParams.viewPractice) {
        this.practice = null;
      }
    },
    countries() {
      this.filterPractices();
    },
    sectors() {
      this.filterPractices();
    },
    methodologies() {
      this.filterPractices();
    },
    documentationTypes() {
      this.filterPractices();
    },
    search() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(this.filterPractices, 1000);
    },
  },
  computed: {
    practiceBackgroundInfo() {
      if (!this.practice) {
        return null;
      }
      const data = {};
      const fields = {
        Country: "Country",
        Sector: "Sector",
        Methodology: "Methodology",
        Organisation: "Organisation",
        Name: "Name",
        Sectors_covered: "Sectors covered",
        Service_categories_covered: "Service categories covered",
        Vehicle_type_covered: "Vehicle type covered",
        Purpose: "Purpose",
        Data_collection_purpose: "Data collection purpose",
      };

      for (let key of Object.keys(fields)) {
        if (this.practice[key]) data[fields[key]] = this.practice[key];
      }

      return data;
    },
    practiceDataCollectionInfo() {
      if (!this.practice) {
        return null;
      }
      const data = {};
      const fields = {
        Sources: "Sources",
        Data_collected: "Data collected",
        Elements_collected: "Elements collected",
        Sample_design: "Sample design",
        Sample_sources: "Sample sources",
        Sample_Population_size: "Sample/Population size",
        Equipment_used: "Equipment used",
        Response_rate: "Response rate",
        Frequency: "Frequency",
        Time_to_complete: "Time to complete",
        Mandatory: "Mandatory",
        Incentive: "Incentive",
        Survey_respondents: "Survey respondents",
        Collection_methods: "Collection methods",
        Who_took_measurements: "Who took measurements",
        End_uses_covered: "End uses covered",
        Geo_climatic_measurements: "Geo-climatic measurements",
        Model_type: "Model type",
        Results_validated: "Results validated",
        Key_model_inputs: "Key model inputs",
        Key_model_outputs: "Key model outputs",
        Population_description: "Population description",
      };

      for (let key of Object.keys(fields)) {
        if (this.practice[key]) data[fields[key]] = this.practice[key];
      }

      return data;
    },
    practiceCommentsInfo() {
      if (!this.practice) {
        return null;
      }
      const data = {};
      const fields = {
        Main_challenges: "Main challenges",
        Key_best_practice: "Key best practice",
        Recommendations: "Recommendations",
        Available_documentation: "Available documentation",
        Additional_observations: "Additional observations",
        Contact: "Contact",
      };

      for (let key of Object.keys(fields)) {
        if (this.practice[key]) data[fields[key]] = this.practice[key];
      }

      return data;
    },
  },
  methods: {
    formatPractice(practice) {
      Object.keys(practice).forEach((key) => {
        if (practice[key] && typeof practice[key] === "string") {
          practice[key] = practice[key].replace(/""/g, '"');
        }
      });

      return { ...practice };
    },
    formatMultiselectLabel(selected) {
      if (!selected || !selected.length) {
        return '0 selected';
      }

      return selected.join(', ');
    },
    fetchPractices() {
      axios
        .get(this.apiRoute)
        .then((response) => {
          this.allPractices = response.data.map((item) =>
            this.formatPractice(item)
          );
          this.practices = this.allPractices;
        })
        .catch((error) => console.log("error fetching data:", error));
    },
    fetchPractice(id) {
      axios
        .get(`${this.apiRoute}/${id}`)
        .then((response) => {
          if (!response.data) {
            this.pageParams = {};
          } else {
            this.practice = this.formatPractice(response.data);
          }
        })
        .catch((error) => console.log("error fetching data:", error));
    },
    openPracticePage(practice) {
      this.practice = practice;
      this.pageParams = { viewPractice: practice.Link_ID };

      let target = document.getElementById("app");
      if (target) {
        const rect = target.getBoundingClientRect();
        const top = rect.top + (document.documentElement.scrollTop || document.body.scrollTop);
        let offsetTarget = Math.max(0, top - 60 + 4);
        window.scrollTo(0, offsetTarget);
      }
    },
    closePracticePage() {
      this.practice = null;
      this.pageParams = {};
      if (!this.practices || !this.practices.length) {
        this.fetchPractices();
        this.filterPractices();
      }
    },
    filterPractices() {
      const { countries, sectors, methodologies, documentationTypes, search } = this;

      const filteredPractices = this.allPractices.filter((item) => {
        const hasAnyCountry = countries.length
          ? countries.some((country) => item.Country.includes(country))
          : true;
        const hasAnySector = sectors.length
          ? sectors.some((sector) => item.Sector.includes(sector))
          : true;
        const hasAnyMethodology = methodologies.length
          ? methodologies.some((methodology) =>
              item.Methodology.includes(methodology)
            )
          : true;
        
        const hasAnyDocumentationType = documentationTypes.length
          ? documentationTypes.some((documentationType) =>
              item.Documentation_type && item.Documentation_type.includes(documentationType)
            )
          : true;

        const hasSearch = search
          ? Object.values(item)
              .join("")
              .toLowerCase()
              .includes(search.toLowerCase())
          : true;
        return hasAnyCountry && hasAnySector && hasAnyMethodology && hasAnyDocumentationType && hasSearch;
      });

      this.practices = filteredPractices;
    },
    resetFilters() {
      this.countries = [];
      this.sectors = [];
      this.methodologies = [];
      this.documentationTypes = [];
      this.search = "";
    },
  },
};
</script>
<style>
/* .m-datatable-table {
  table-layout: fixed;
} */
.a-dropdown__option--selected {
  background: #f3f3f3;
  display: flex !important;
  align-items: center;
  justify-content: space-between;
}
.a-dropdown.a-dropdown--select {
  width: 50% !important;
}
.filter-actions {
  display: flex;
  align-items: center;
}
.filter-actions > button {
  margin-right: 20px;
}
.icon {
  min-width: unset !important;
}
.m-nav-page__infos-close-btn {
  margin-bottom: 24px;
  margin-right: 0;
  box-shadow: none !important;
  cursor: pointer;
}
</style>